import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Compass, MountainSnow, PenTool, Radio } from "lucide-react";
import { EXPERIENCE } from "../data/content";
import { Reveal, SectionHeader, TopoLines } from "./ui-bits";

const CATS = ["ALL OPS", "DESIGN & WEB", "MARKETING & SEO", "EXPEDITION"];
const CAT_ICON = { "DESIGN & WEB": PenTool, "MARKETING & SEO": Compass, EXPEDITION: MountainSnow };
const ALT = {
  log01: "ALT 01 // 1,200 M",
  log02: "ALT 02 // 2,100 M",
  log03: "ALT 03 // 3,400 M",
  log04: "ALT 04 // 4,800 M",
  log05: "ALT 05 // 5,200 M",
  log06: "SUMMIT BASE // LIVE",
};

function Beacon() {
  return (
    <div className="mt-5 flex items-center gap-3 border border-ember/40 bg-ember/10 px-3.5 py-2.5" data-testid="experience-current-beacon">
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute h-full w-full animate-ping rounded-full bg-ember opacity-70" />
        <span className="relative h-2.5 w-2.5 rounded-full bg-ember" />
      </span>
      <span className="font-mono text-[9px] font-bold tracking-[0.25em] text-ember">ACTIVE OPERATIONAL HEADQUARTERS</span>
      <Radio className="ml-auto h-3.5 w-3.5 shrink-0 text-ember" />
    </div>
  );
}

function DossierCard({ e }) {
  const Icon = CAT_ICON[e.cat];
  return (
    <motion.div
      key={e.id}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="clip-tag relative overflow-hidden border border-white/10 bg-panel p-6 md:p-8"
      data-testid={`experience-dossier-card-${e.id}`}
    >
      <TopoLines className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.05]" />
      <div className="relative">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="border border-ember/40 px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.25em] text-ember">{ALT[e.id]}</span>
          <span className="flex items-center gap-2 font-mono text-[9px] tracking-[0.2em] text-cream/60">
            <Icon className="h-3.5 w-3.5" /> {e.cat}
          </span>
        </div>
        <h3 className="mt-5 font-display text-2xl font-black uppercase leading-tight text-white md:text-4xl">{e.org}</h3>
        <p className="mt-2 font-mono text-[11px] tracking-[0.2em] text-ember md:text-xs">{e.role}</p>
        <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-cream/50" data-testid={`experience-period-${e.id}`}>{e.period}</p>
        {e.current && <Beacon />}
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300 md:text-base">{e.summary}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {e.tags.map((t) => (
            <span key={t} className="border border-white/15 px-2.5 py-1 font-mono text-[9px] tracking-[0.18em] text-slate-300">{t}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function Waypoint({ e, active, onClick }) {
  return (
    <div className="relative">
      <span
        className={`absolute -left-[27px] top-9 z-10 h-2.5 w-2.5 rotate-45 border-2 transition-colors duration-300 ${active ? "border-ember bg-ember" : "border-slate-600 bg-night"}`}
        aria-hidden="true"
      />
      <button
        onClick={onClick}
        data-testid={`experience-log-toggle-${e.id}`}
        className={`w-full border p-4 text-left transition-all duration-300 md:p-5 ${active ? "border-ember bg-ember/10 shadow-[0_0_28px_rgba(255,107,55,0.14)]" : "border-white/10 bg-ink/60 hover:border-white/25"}`}
      >
        <div className="flex items-center justify-between gap-3">
          <span className={`font-mono text-[9px] font-bold tracking-[0.25em] ${active ? "text-ember" : "text-cream/40"}`}>{ALT[e.id]}</span>
          {e.current && (
            <span className="flex items-center gap-1.5 font-mono text-[8px] font-bold tracking-[0.25em] text-ember">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ember" /> LIVE
            </span>
          )}
        </div>
        <p className="mt-2 font-display text-lg font-bold uppercase leading-tight text-white md:text-xl">{e.org}</p>
        <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-slate-400">{e.role}</p>
        <p className="mt-1 font-mono text-[9px] tracking-[0.18em] text-cream/50" data-testid={`experience-period-${e.id}`}>{e.period}</p>
      </button>
      {/* inline dossier on mobile */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden lg:hidden"
          >
            <div className="pt-3">
              <DossierCard e={e} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Experience() {
  const [cat, setCat] = useState("ALL OPS");
  const [activeId, setActiveId] = useState("log06");

  const rows = EXPERIENCE.filter((e) => cat === "ALL OPS" || e.cat === cat);
  const active = rows.find((e) => e.id === activeId) ?? rows[0];

  return (
    <section id="experience" data-testid="experience-section" className="relative scroll-mt-20 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeader
          index="04"
          station="Work Experience"
          id="LOG // DISPATCHES FROM THE FIELD"
          title={<>Field Ops<br /><span className="text-ember">Experience</span></>}
          sub="Every role is a dispatch from the field. Walk the trail, open a camp and read its dossier — filter by discipline."
        />

        <Reveal>
          <div className="mb-10 flex flex-wrap items-center gap-2 border-b border-white/10 pb-5" data-testid="experience-filter">
            {CATS.map((c) => {
              const n = c === "ALL OPS" ? EXPERIENCE.length : EXPERIENCE.filter((e) => e.cat === c).length;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`experience-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-colors ${cat === c ? "bg-ember font-bold text-night" : "btn-fill"}`}
                >
                  {c} <span className={`ml-1 ${cat === c ? "text-night/70" : "text-ember"}`}>{String(n).padStart(2, "0")}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="relative space-y-3 border-l-2 border-white/10 pl-6 lg:col-span-5">
            <Reveal>
              <div className="space-y-3">
                {rows.map((e) => (
                  <Waypoint key={e.id} e={e} active={active?.id === e.id} onClick={() => setActiveId(e.id)} />
                ))}
              </div>
            </Reveal>
          </div>
          <div className="hidden lg:col-span-7 lg:sticky lg:top-24 lg:block lg:self-start">
            <AnimatePresence mode="wait">{active && <DossierCard key={active.id} e={active} />}</AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
